/* eslint-disable react/prop-types */
import React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "contexts/AuthContext";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Swal from "sweetalert2";
import Dialog from "@mui/material/Dialog";
import { Slide, Chip, Tabs, Tab } from "@mui/material";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import StatusChip from "components/StatusChip";
import { API_BASE, getStorageUrl } from "config/api";

// Cell Components
const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);

const MemberIdCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value || "-"}
  </Typography>
);

const FirstNameCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value || "-"}
  </Typography>
);

const MiddleNameCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value || "-"}
  </Typography>
);

const LastNameCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value || "-"}
  </Typography>
);

const CountryCell = ({ value }) => (
  <Box display="flex" alignItems="center" gap={0.5}>
    <Icon sx={{ fontSize: 16, color: "info.main" }}>flag</Icon>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {value || "-"}
    </Typography>
  </Box>
);

const DepartmentCell = ({ value }) => (
  <Box lineHeight={1} textAlign="left">
    <Typography display="block" variant="button" fontWeight={600}>
      {value || "No Department"}
    </Typography>
  </Box>
);

const StatusCell = ({ value }) => (
  <StatusChip value={value} color={value === "active" ? "success" : "error"} />
);

const PhotoCell = ({ value }) => {
  const [imgError, setImgError] = useState(false);
  const imageUrl = getStorageUrl(value);

  if (!value || imgError) {
    return (
      <Avatar sx={{ width: 35, height: 35, bgcolor: "#1976d2" }}>
        <Icon sx={{ fontSize: 20, color: "white" }}>person</Icon>
      </Avatar>
    );
  }

  return (
    <Avatar
      src={imageUrl}
      sx={{ width: 35, height: 35 }}
      onError={() => setImgError(true)}
      alt="Member"
    />
  );
};

const ActionsCell = ({ row, handleEdit, handleDelete, handleShow, handleSendCredentials }) => (
  <Box display="flex" justifyContent="center" gap={0.5} flexWrap="wrap">
    <Tooltip title="Show">
      <IconButton size="small" color="info" onClick={() => handleShow(row.original)}>
        <Icon fontSize="small">visibility</Icon>
      </IconButton>
    </Tooltip>
    <Tooltip title="Edit">
      <IconButton size="small" color="primary" onClick={() => handleEdit(row.original)}>
        <Icon fontSize="small">edit</Icon>
      </IconButton>
    </Tooltip>
    {handleSendCredentials && (
      <Tooltip title="Send login credentials">
        <IconButton
          size="small"
          color="secondary"
          onClick={() => handleSendCredentials(row.original)}
        >
          <Icon fontSize="small">key</Icon>
        </IconButton>
      </Tooltip>
    )}
    <Tooltip title="Delete">
      <IconButton size="small" color="error" onClick={() => handleDelete(row.original)}>
        <Icon fontSize="small">delete</Icon>
      </IconButton>
    </Tooltip>
  </Box>
);

ActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleShow: PropTypes.func.isRequired,
  handleSendCredentials: PropTypes.func,
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DetailItem = ({ label, value, icon }) => (
  <Box
    mb={2}
    p={2}
    sx={{
      backgroundColor: "rgba(0,0,0,0.02)",
      borderRadius: "8px",
      border: "1px solid rgba(0,0,0,0.1)",
    }}
  >
    <Box display="flex" alignItems="center" mb={0.5}>
      {icon && <Icon sx={{ mr: 1, color: "info.main" }}>{icon}</Icon>}
      <Typography variant="caption" fontWeight={700} color="info.main">
        {label}
      </Typography>
    </Box>
    <Typography variant="body2" sx={{ pl: icon ? 4 : 0 }}>
      {value || "Not provided"}
    </Typography>
  </Box>
);

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  icon: PropTypes.string,
};

/**
 * Modern member-details modal:
 *  - Flat header with avatar, name, member ID and status chip
 *  - Tabbed body (Personal · Contact · Family · Church · Files)
 *  - Sticky footer with Close action
 */
const MemberDetailsModal = ({ member, open, onClose }) => {
  const [tab, setTab] = useState(0);
  const [imgError, setImgError] = useState(false);
  const [photoLoadError, setPhotoLoadError] = useState(false);

  // Reset when the dialog is reopened with a new member
  useEffect(() => {
    if (open) {
      setTab(0);
      setImgError(false);
      setPhotoLoadError(false);
    }
  }, [open, member?.id]);

  if (!member) return null;

  const fullName = [member.firstName, member.middleName, member.surname].filter(Boolean).join(" ");
  const isActive = member.status === "active" || member.status === true || member.status === 1;
  const photoUrl = !imgError ? getStorageUrl(member.photo) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionComponent={Transition}
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* ---------- HEADER ---------- */}
      <Box
        sx={{
          position: "relative",
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          px: { xs: 3, sm: 4 },
          py: { xs: 3, sm: 3.5 },
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 12, right: 12, color: "text.secondary" }}
        >
          <Icon>close</Icon>
        </IconButton>

        <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
          {photoUrl ? (
            <Avatar
              src={photoUrl}
              alt={fullName}
              sx={{ width: 88, height: 88, border: "1px solid", borderColor: "divider" }}
              onError={() => setImgError(true)}
            />
          ) : (
            <Avatar
              sx={{
                width: 88,
                height: 88,
                bgcolor: "primary.main",
                color: "white",
                fontSize: 30,
                fontWeight: 700,
              }}
            >
              {(member.firstName?.[0] || "?").toUpperCase()}
              {(member.surname?.[0] || "").toUpperCase()}
            </Avatar>
          )}

          <Box flexGrow={1}>
            <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {fullName || "Unnamed Member"}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
              <Chip
                icon={<Icon sx={{ fontSize: 18 }}>badge</Icon>}
                label={member.memberId || "No ID"}
                size="small"
                variant="outlined"
              />
              <StatusChip
                value={isActive ? "active" : "inactive"}
                color={isActive ? "success" : "error"}
              />
              {member.country && (
                <Chip
                  icon={<Icon sx={{ fontSize: 18 }}>public</Icon>}
                  label={member.country}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ---------- TABS ---------- */}
      <Box sx={{ bgcolor: "white" }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 48 },
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Tab icon={<Icon fontSize="small">person</Icon>} iconPosition="start" label="Personal" />
          <Tab icon={<Icon fontSize="small">contacts</Icon>} iconPosition="start" label="Contact" />
          <Tab
            icon={<Icon fontSize="small">family_restroom</Icon>}
            iconPosition="start"
            label="Family"
          />
          <Tab icon={<Icon fontSize="small">church</Icon>} iconPosition="start" label="Church" />
          <Tab
            icon={<Icon fontSize="small">attach_file</Icon>}
            iconPosition="start"
            label="Files"
          />
        </Tabs>

        <Box p={{ xs: 2, sm: 3 }} sx={{ maxHeight: "55vh", overflowY: "auto" }}>
          {tab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DetailItem label="First Name" value={member.firstName} icon="person" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Middle Name" value={member.middleName} icon="person_outline" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Surname" value={member.surname} icon="person" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Gender" value={member.gender} icon="wc" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Date of Birth" value={member.dateOfBirth} icon="cake" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Place of Birth" value={member.placeOfBirth} icon="place" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Nationality" value={member.nationality} icon="public" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Marital Status" value={member.maritalStatus} icon="favorite" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Occupation" value={member.occupation} icon="work" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Education" value={member.education} icon="school" />
              </Grid>
            </Grid>
          )}

          {tab === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Phone" value={member.phoneNumber} icon="phone" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="WhatsApp" value={member.whatsappNumber} icon="chat" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Other Number" value={member.otherNumber} icon="call" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Email" value={member.email} icon="email" />
              </Grid>
              <Grid item xs={12}>
                <DetailItem
                  label="Residential Address"
                  value={member.residentialAddress}
                  icon="home"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Postal Address"
                  value={member.postalAddress}
                  icon="markunread_mailbox"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Region" value={member.region} icon="map" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Home Town" value={member.homeTown} icon="location_city" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Country" value={member.country} icon="flag" />
              </Grid>
            </Grid>
          )}

          {tab === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Spouse" value={member.spouseName} icon="favorite" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Spouse Phone" value={member.spousePhoneNumber} icon="phone" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DetailItem label="Children" value={member.numberOfChildren} icon="child_care" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DetailItem label="Sons" value={member.numberOfMales} icon="male" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DetailItem label="Daughters" value={member.numberOfFemales} icon="female" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Father" value={member.nameOfFather} icon="man" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Mother" value={member.nameOfMother} icon="woman" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Next of Kin" value={member.nextOfKin} icon="contact_emergency" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Next of Kin Phone" value={member.nextOfKinPhone} icon="phone" />
              </Grid>
            </Grid>
          )}

          {tab === 3 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Department" value={member.department} icon="group" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Ministry" value={member.ministry} icon="volunteer_activism" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Baptized" value={member.areYouBaptized} icon="water_drop" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Baptism Date" value={member.baptismDate} icon="event" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Baptism Church" value={member.baptismChurch} icon="church" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Year of Conversion"
                  value={member.yearOfConversion}
                  icon="auto_awesome"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Pays Tithe" value={member.willYouPayTithe} icon="payments" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Pays Welfare"
                  value={member.willYouPayWelfare}
                  icon="health_and_safety"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Membership Date" value={member.membershipDate} icon="badge" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem label="Role" value={member.role} icon="admin_panel_settings" />
              </Grid>
            </Grid>
          )}

          {tab === 4 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" fontWeight={700} color="info.main">
                  <Icon sx={{ verticalAlign: "middle", mr: 0.5, fontSize: 16 }}>image</Icon>
                  Photo
                </Typography>
                <Box
                  mt={1}
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid rgba(0,0,0,0.08)",
                    backgroundColor: "rgba(0,0,0,0.03)",
                    aspectRatio: "1 / 1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {member.photo && !photoLoadError ? (
                    <img
                      src={getStorageUrl(member.photo)}
                      alt="Member"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={() => setPhotoLoadError(true)}
                    />
                  ) : (
                    <Icon sx={{ fontSize: 64, color: "text.disabled" }}>image_not_supported</Icon>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" fontWeight={700} color="info.main">
                  <Icon sx={{ verticalAlign: "middle", mr: 0.5, fontSize: 16 }}>draw</Icon>
                  Signature
                </Typography>
                <Box
                  mt={1}
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid rgba(0,0,0,0.08)",
                    backgroundColor: "white",
                    p: 1,
                    minHeight: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {member.signature ? (
                    <img
                      src={getStorageUrl(member.signature)}
                      alt="Signature"
                      style={{ maxWidth: "100%", maxHeight: 120, objectFit: "contain" }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      No signature on file
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>

      {/* ---------- FOOTER ---------- */}
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        px={{ xs: 2, sm: 3 }}
        py={2}
        mt={2}
        sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", bgcolor: "rgba(0,0,0,0.02)" }}
      >
        <Button variant="contained" color="info" onClick={onClose} startIcon={<Icon>close</Icon>}>
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

MemberDetailsModal.propTypes = {
  member: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default function useMemberData() {
  const { church } = useAuth();
  const abortControllerRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);

  const baseColumns = [
    { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
    { Header: "Photo", accessor: "photo", align: "center", width: "8%", Cell: PhotoCell },
    { Header: "MemberID", accessor: "memberId", align: "center", width: "12%", Cell: MemberIdCell },
    {
      Header: "First Name",
      accessor: "firstName",
      align: "left",
      width: "10%",
      Cell: FirstNameCell,
    },
    {
      Header: "Middle Name",
      accessor: "middleName",
      align: "left",
      width: "10%",
      Cell: MiddleNameCell,
    },
    { Header: "Surname", accessor: "surname", align: "left", width: "10%", Cell: LastNameCell },
    { Header: "Country", accessor: "country", align: "left", width: "8%", Cell: CountryCell },
    { Header: "Gender", accessor: "gender", align: "left", width: "6%" },
    {
      Header: "Department",
      accessor: "department",
      align: "left",
      width: "12%",
      Cell: DepartmentCell,
    },
    { Header: "Status", accessor: "status", align: "center", width: "8%", Cell: StatusCell },
  ];

  const [memberData, setMemberData] = useState({
    columns: baseColumns,
    rows: [],
    filteredRows: [],
    isLoading: false,
    error: null,
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [showModalOpen, setShowModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [branches, setBranches] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMinistry, setFilterMinistry] = useState("");
  const [filterGender, setFilterGender] = useState("");
  const [filterAge, setFilterAge] = useState("");

  const handlePhotoUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      console.log("Photo uploaded, preview URL:", previewUrl);
      setPhotoPreview(previewUrl);
      setSelectedMember((prev) => ({ ...prev, photoFile: file, photoPreview: previewUrl }));
    }
  }, []);

  const handleSignatureUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setSignaturePreview(previewUrl);
      setSelectedMember((prev) => ({ ...prev, signatureFile: file, signaturePreview: previewUrl }));
    }
  }, []);

  const handleShow = useCallback((member) => {
    setSelectedMember(member);
    setShowModalOpen(true);
  }, []);

  const handleEdit = useCallback((member) => {
    console.log("=== handleEdit called ===");
    console.log("Member data received:", member);
    console.log("Member photo path:", member.photo);
    console.log("Member areYouBaptized:", member.areYouBaptized);

    setPhotoPreview(null);
    setSignaturePreview(null);

    let photoUrl = null;
    if (member.photo) {
      photoUrl = getStorageUrl(member.photo);
      console.log("Generated photo URL:", photoUrl);
    }

    setSelectedMember({
      ...member,
      memberId: member.memberId || "",
      photoFile: null,
      signatureFile: null,
      photoPreview: photoUrl,
      signaturePreview: null,
      areYouBaptized: member.areYouBaptized || "",
    });

    console.log("Selected member set with areYouBaptized:", member.areYouBaptized || "");
    setEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((member) => {
    setSelectedMember({ ...member, memberName: `${member.firstName} ${member.surname}` });
    setDeleteModalOpen(true);
  }, []);

  // Send (or resend) the default login credentials to a single member.
  const handleSendCredentials = useCallback(async (member) => {
    const confirm = await Swal.fire({
      title: "Send login credentials?",
      html:
        `<div style="text-align:left">` +
        `<p><strong>${member.firstName || ""} ${member.surname || ""}</strong></p>` +
        `<p>They'll receive their username + default password ` +
        `<strong>12345678</strong>.</p>` +
        `<p style="font-size:.9em;color:#666">${
          (member.country || "Ghana") === "Ghana"
            ? "Channel: SMS to " + (member.phone || "?")
            : "Channel: Email to " + (member.email || "?")
        }</p></div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Send",
    });
    if (!confirm.isConfirmed) return;

    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE}/members/${member.id}/send-credentials`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json();
      if (!res.ok || json?.data?.delivered === false) {
        throw new Error(json?.data?.error || json?.message || "Could not deliver credentials");
      }
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: `Sent via ${json.data.channel.toUpperCase()}`,
        showConfirmButton: false,
        timer: 2500,
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Send failed", text: e.message });
    }
  }, []);

  useEffect(() => {
    const actionsColumn = {
      Header: "Actions",
      accessor: "actions",
      align: "center",
      width: "15%",
      Cell: (props) => (
        <ActionsCell
          {...props}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleShow={handleShow}
          handleSendCredentials={handleSendCredentials}
        />
      ),
    };
    setMemberData((prev) => ({ ...prev, columns: [...baseColumns, actionsColumn] }));
  }, [handleEdit, handleDelete, handleShow, handleSendCredentials]);

  const fetchDepartments = useCallback(async () => {
    if (!church?.id) return;
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE}/departments/church/${church.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setDepartments(data.data || data.departments || data);
    } catch (error) {
      console.error("Failed to fetch departments:", error);
    }
  }, [church?.id]);

  const fetchMinistries = useCallback(async () => {
    if (!church?.id) return;
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE}/ministries/church/${church.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setMinistries(data.data || data.ministries || data);
    } catch (error) {
      console.error("Failed to fetch ministries:", error);
    }
  }, [church?.id]);

  const fetchBranches = useCallback(async () => {
    if (!church?.id) return;
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE}/branches/church/${church.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setBranches(data.data || data.branches || data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  }, [church?.id]);

  const getAgeFromDob = (dob) => {
    if (!dob) return null;
    const diff = Date.now() - new Date(dob).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
  };

  const applyAllFilters = useCallback((rows, query, ministry, gender, ageGroup) => {
    const lowerQuery = (query || "").toLowerCase().trim();
    return rows.filter((row) => {
      if (lowerQuery) {
        const fullName = [row.firstName, row.middleName, row.surname]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        const memberId = (row.memberId || "").toLowerCase();
        const phone = (row.phoneNumber || "").toLowerCase();
        const country = (row.country || "").toLowerCase();
        if (
          !fullName.includes(lowerQuery) &&
          !memberId.includes(lowerQuery) &&
          !phone.includes(lowerQuery) &&
          !country.includes(lowerQuery)
        )
          return false;
      }
      if (ministry && (row.ministry || "") !== ministry) return false;
      if (gender && (row.gender || "").toLowerCase() !== gender.toLowerCase()) return false;
      if (ageGroup) {
        const age = getAgeFromDob(row.dateOfBirth);
        if (age === null) return false;
        if (ageGroup === "0-17" && !(age >= 0 && age <= 17)) return false;
        if (ageGroup === "18-35" && !(age >= 18 && age <= 35)) return false;
        if (ageGroup === "36-50" && !(age >= 36 && age <= 50)) return false;
        if (ageGroup === "51+" && !(age >= 51)) return false;
      }
      return true;
    });
  }, []);

  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      setMemberData((prev) => ({
        ...prev,
        filteredRows: applyAllFilters(prev.rows, query, filterMinistry, filterGender, filterAge),
      }));
    },
    [applyAllFilters, filterMinistry, filterGender, filterAge]
  );

  const handleFilterMinistry = useCallback(
    (value) => {
      setFilterMinistry(value);
      setMemberData((prev) => ({
        ...prev,
        filteredRows: applyAllFilters(prev.rows, searchQuery, value, filterGender, filterAge),
      }));
    },
    [applyAllFilters, searchQuery, filterGender, filterAge]
  );

  const handleFilterGender = useCallback(
    (value) => {
      setFilterGender(value);
      setMemberData((prev) => ({
        ...prev,
        filteredRows: applyAllFilters(prev.rows, searchQuery, filterMinistry, value, filterAge),
      }));
    },
    [applyAllFilters, searchQuery, filterMinistry, filterAge]
  );

  const handleFilterAge = useCallback(
    (value) => {
      setFilterAge(value);
      setMemberData((prev) => ({
        ...prev,
        filteredRows: applyAllFilters(prev.rows, searchQuery, filterMinistry, filterGender, value),
      }));
    },
    [applyAllFilters, searchQuery, filterMinistry, filterGender]
  );

  const fetchMembers = useCallback(async () => {
    if (!church?.id) return;
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      setMemberData((prev) => ({ ...prev, isLoading: true }));
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE}/members/church/${church.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortControllerRef.current.signal,
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const members = result.data || result.members || result;

      console.log("=== API Response Sample ===");
      if (members && members.length > 0) {
        console.log("First member from API:", members[0]);
      }

      const rows = members.map((member, index) => ({
        id: member.id,
        serial: index + 1,
        memberId: member.memberID || "-",
        firstName: member.first_name || "",
        middleName: member.middle_name || "",
        surname: member.surname || "",
        gender: member.gender || "-",
        department: member.department?.name || "No Department",
        departmentId: member.department?.id,
        ministry: member.ministry?.name || "",
        ministryId: member.ministry?.id,
        branch: member.branch?.name || "",
        branchId: member.branch?.id,
        status: member.status ? "active" : "inactive",
        areYouBaptized: (() => {
          const raw = member.are_you_baptize || member.are_you_baptized || "";
          const l = raw.toLowerCase();
          return l === "yes" ? "Yes" : l === "no" ? "No" : raw;
        })(),
        photo: member.photo || "",
        phoneNumber: member.phone || "",
        otherNumber: member.other_number || "",
        whatsappNumber: member.whatsapp_number || "",
        email: member.email || "",
        residentialAddress: member.residential_address || "",
        postalAddress: member.postal_address || "",
        region: member.region || "",
        homeTown: member.home_town || "",
        country: member.country || "",
        country_code: member.country_code || "",
        phone_country_code: member.phone_country_code || "",
        nationality: member.nationality || "",
        placeOfBirth: member.place_of_birth || "",
        dateOfBirth: member.date_of_birth || "",
        maritalStatus: member.marital_status || "",
        occupation: member.occupation || "",
        profession: member.profession || "",
        rank: member.rank || "",
        employment: member.employment || "",
        nameOfEmployer: member.name_of_employer || "",
        education: member.education || "",
        spouseName: member.spouse_name || "",
        spousePhoneNumber: member.spouse_phone || "",
        dateOfMarriage: member.date_of_marriage || "",
        byPastor: member.by_pastor || "",
        numberOfChildren: member.number_of_children || 0,
        numberOfMales: member.number_of_male || 0,
        numberOfFemales: member.number_of_female || 0,
        nameOfFather: member.name_of_father || "",
        fatherLifeStatus: member.father_life_status || "",
        nameOfMother: member.name_of_mother || "",
        motherLifeStatus: member.mother_life_status || "",
        nextOfKin: member.next_of_kin || "",
        nextOfKinPhone: member.next_of_kin_phone || "",
        nextOfKinRelationship: member.next_of_kin_relationship || "",
        yearOfConversion: member.year_of_conversion || "",
        baptismType: member.baptism_type || "",
        baptismDate: member.baptism_date || "",
        baptismChurch: member.baptism_church || "",
        baptismBy: member.baptism_by || "",
        baptismCertificate: member.baptism_certificate || "",
        nameOfPreviousChurch: member.name_of_previous_church || "",
        previousChurchAddress: member.previous_church_address || "",
        placeOfInterest: member.place_of_interest || "",
        willYouPayTithe: member.will_you_pay_tithe || "",
        communicant: member.communicant || "",
        willYouPayWelfare: member.will_you_pay_welfare || "",
        membershipDate: member.membership_date || "",
        role: member.role || "member",
        signature: member.signature || "",
        languagesSpoken: member.languages || "",
        originalData: member,
      }));

      setMemberData((prev) => ({
        ...prev,
        rows,
        filteredRows: rows,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      if (error.name !== "AbortError") {
        setMemberData((prev) => ({ ...prev, isLoading: false, error: error.message }));
        console.error("Fetch failed:", error);
      }
    }
  }, [church?.id]);

  const handleEditSubmit = useCallback(async () => {
    try {
      // Validation
      if (!selectedMember?.firstName || !selectedMember?.surname) {
        Swal.fire({
          title: "Error!",
          text: "First name and surname are required",
          icon: "error",
          toast: true,
          timer: 3000,
        });
        return;
      }

      if (!selectedMember?.membershipDate) {
        Swal.fire({
          title: "Error!",
          text: "Membership Date is required",
          icon: "error",
          toast: true,
          timer: 3000,
        });
        return;
      }

      setIsSaving(true);
      const token = localStorage.getItem("authToken");
      const url = selectedMember.id
        ? `${API_BASE}/members/${selectedMember.id}`
        : `${API_BASE}/members`;
      const formData = new FormData();

      // Personal Information
      if (selectedMember.firstName) formData.append("first_name", selectedMember.firstName);
      if (selectedMember.middleName) formData.append("middle_name", selectedMember.middleName);
      if (selectedMember.surname) formData.append("surname", selectedMember.surname);
      if (selectedMember.gender) formData.append("gender", selectedMember.gender);
      if (selectedMember.dateOfBirth) formData.append("date_of_birth", selectedMember.dateOfBirth);
      if (selectedMember.maritalStatus)
        formData.append("marital_status", selectedMember.maritalStatus);
      if (selectedMember.phoneNumber) formData.append("phone", selectedMember.phoneNumber);
      // Send country code only when phone is in local format (no leading +).
      // When phone already includes +country_code, skip to avoid double-prefixing.
      if (
        selectedMember.phone_country_code &&
        !String(selectedMember.phoneNumber || "").startsWith("+")
      ) {
        formData.append("phone_country_code", selectedMember.phone_country_code);
      }
      if (selectedMember.email) formData.append("email", selectedMember.email);
      if (selectedMember.residentialAddress)
        formData.append("residential_address", selectedMember.residentialAddress);
      if (selectedMember.country) formData.append("country", selectedMember.country);
      if (selectedMember.areYouBaptized)
        formData.append("are_you_baptize", selectedMember.areYouBaptized);
      if (selectedMember.departmentId)
        formData.append("department_id", selectedMember.departmentId);
      if (selectedMember.ministryId) formData.append("ministry_id", selectedMember.ministryId);
      if (selectedMember.branchId) formData.append("branch_id", selectedMember.branchId);
      if (selectedMember.membershipDate)
        formData.append("membership_date", selectedMember.membershipDate);
      if (selectedMember.role) formData.append("role", selectedMember.role);
      if (selectedMember.languagesSpoken)
        formData.append("languages", selectedMember.languagesSpoken);

      // Additional fields
      if (selectedMember.postalAddress)
        formData.append("postal_address", selectedMember.postalAddress);
      if (selectedMember.region) formData.append("region", selectedMember.region);
      if (selectedMember.homeTown) formData.append("home_town", selectedMember.homeTown);
      if (selectedMember.nationality) formData.append("nationality", selectedMember.nationality);
      if (selectedMember.placeOfBirth)
        formData.append("place_of_birth", selectedMember.placeOfBirth);
      if (selectedMember.occupation) formData.append("occupation", selectedMember.occupation);
      if (selectedMember.profession) formData.append("profession", selectedMember.profession);
      if (selectedMember.rank) formData.append("rank", selectedMember.rank);
      if (selectedMember.employment) formData.append("employment", selectedMember.employment);
      if (selectedMember.nameOfEmployer)
        formData.append("name_of_employer", selectedMember.nameOfEmployer);
      if (selectedMember.education) formData.append("education", selectedMember.education);
      if (selectedMember.spouseName) formData.append("spouse_name", selectedMember.spouseName);
      if (selectedMember.spousePhoneNumber)
        formData.append("spouse_phone", selectedMember.spousePhoneNumber);
      if (selectedMember.dateOfMarriage)
        formData.append("date_of_marriage", selectedMember.dateOfMarriage);
      if (selectedMember.byPastor) formData.append("by_pastor", selectedMember.byPastor);
      if (selectedMember.numberOfChildren !== undefined)
        formData.append("number_of_children", selectedMember.numberOfChildren || 0);
      if (selectedMember.numberOfMales !== undefined)
        formData.append("number_of_male", selectedMember.numberOfMales || 0);
      if (selectedMember.numberOfFemales !== undefined)
        formData.append("number_of_female", selectedMember.numberOfFemales || 0);
      if (selectedMember.nameOfFather)
        formData.append("name_of_father", selectedMember.nameOfFather);
      if (selectedMember.fatherLifeStatus)
        formData.append("father_life_status", selectedMember.fatherLifeStatus);
      if (selectedMember.nameOfMother)
        formData.append("name_of_mother", selectedMember.nameOfMother);
      if (selectedMember.motherLifeStatus)
        formData.append("mother_life_status", selectedMember.motherLifeStatus);
      if (selectedMember.nextOfKin) formData.append("next_of_kin", selectedMember.nextOfKin);
      if (selectedMember.nextOfKinPhone)
        formData.append("next_of_kin_phone", selectedMember.nextOfKinPhone);
      if (selectedMember.nextOfKinRelationship)
        formData.append("next_of_kin_relationship", selectedMember.nextOfKinRelationship);
      if (selectedMember.yearOfConversion)
        formData.append("year_of_conversion", selectedMember.yearOfConversion);
      if (selectedMember.baptismType) formData.append("baptism_type", selectedMember.baptismType);
      if (selectedMember.baptismDate) formData.append("baptism_date", selectedMember.baptismDate);
      if (selectedMember.baptismChurch)
        formData.append("baptism_church", selectedMember.baptismChurch);
      if (selectedMember.baptismBy) formData.append("baptism_by", selectedMember.baptismBy);
      if (selectedMember.nameOfPreviousChurch)
        formData.append("name_of_previous_church", selectedMember.nameOfPreviousChurch);
      if (selectedMember.previousChurchAddress)
        formData.append("previous_church_address", selectedMember.previousChurchAddress);
      if (selectedMember.placeOfInterest)
        formData.append("place_of_interest", selectedMember.placeOfInterest);
      if (selectedMember.willYouPayTithe)
        formData.append("will_you_pay_tithe", selectedMember.willYouPayTithe);
      if (selectedMember.communicant) formData.append("communicant", selectedMember.communicant);
      if (selectedMember.willYouPayWelfare)
        formData.append("will_you_pay_welfare", selectedMember.willYouPayWelfare);

      // Church ID and Status
      if (church?.id) formData.append("church_id", church.id);
      formData.append("status", selectedMember.status === "active" ? "1" : "0");

      // Files
      if (selectedMember.photoFile) formData.append("photo", selectedMember.photoFile);
      if (selectedMember.signatureFile) formData.append("signature", selectedMember.signatureFile);

      // For update, add method override and allow memberID edit
      if (selectedMember.id) {
        formData.append("_method", "PUT");
        if (selectedMember.memberId) formData.append("memberID", selectedMember.memberId);
      }

      console.log("Submitting form with membershipDate:", selectedMember.membershipDate);

      const response = await fetch(url, {
        method: "POST",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.message || "Failed to save member");

      Swal.fire({
        title: "Success!",
        text: "Member saved successfully!",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
      await fetchMembers();
      setEditModalOpen(false);
      setSelectedMember(null);
      setPhotoPreview(null);
      setSignaturePreview(null);
    } catch (error) {
      console.error("Save error:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    } finally {
      setIsSaving(false);
    }
  }, [selectedMember, church?.id, fetchMembers]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE}/members/${selectedMember.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to delete member");
      await fetchMembers();
      Swal.fire("Deleted!", "Member has been deleted.", "success");
    } catch (error) {
      Swal.fire("Error!", error.message, "error");
    } finally {
      setDeleteModalOpen(false);
    }
  }, [selectedMember?.id, fetchMembers]);

  const handleAddMember = useCallback(() => {
    const today = new Date().toISOString().split("T")[0];
    setSelectedMember({
      id: null,
      memberId: "",
      firstName: "",
      middleName: "",
      surname: "",
      gender: "",
      dateOfBirth: "",
      maritalStatus: "",
      country: "",
      phoneNumber: "",
      email: "",
      residentialAddress: "",
      postalAddress: "",
      region: "",
      homeTown: "",
      nationality: "",
      placeOfBirth: "",
      occupation: "",
      profession: "",
      rank: "",
      employment: "",
      nameOfEmployer: "",
      education: "",
      spouseName: "",
      spousePhoneNumber: "",
      dateOfMarriage: "",
      byPastor: "",
      numberOfChildren: 0,
      numberOfMales: 0,
      numberOfFemales: 0,
      nameOfFather: "",
      fatherLifeStatus: "",
      nameOfMother: "",
      motherLifeStatus: "",
      nextOfKin: "",
      nextOfKinPhone: "",
      nextOfKinRelationship: "",
      yearOfConversion: "",
      areYouBaptized: "",
      baptismType: "",
      baptismDate: "",
      baptismChurch: "",
      baptismBy: "",
      nameOfPreviousChurch: "",
      previousChurchAddress: "",
      placeOfInterest: "",
      willYouPayTithe: "",
      communicant: "",
      willYouPayWelfare: "",
      membershipDate: today, // Set today's date as default
      role: "member",
      languagesSpoken: "",
      departmentId: null,
      ministryId: null,
      branchId: null,
      status: "active",
      photoFile: null,
      signatureFile: null,
      photo: "",
      signature: "",
      phone_country_code: "+233",
      country_code: "+233",
    });
    setEditModalOpen(true);
  }, []);

  const handlePrint = useCallback(() => window.print(), []);

  useEffect(() => {
    if (church?.id) {
      fetchMembers();
      fetchDepartments();
      fetchMinistries();
      fetchBranches();
    }
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [church?.id, fetchMembers, fetchDepartments, fetchMinistries, fetchBranches]);

  return {
    columns: memberData.columns,
    rows: memberData.rows,
    filteredRows: memberData.filteredRows,
    isLoading: memberData.isLoading,
    error: memberData.error,
    handleShow,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    showModalOpen,
    setShowModalOpen,
    selectedMember,
    setSelectedMember,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMember,
    departments,
    ministries,
    branches,
    MemberDetailsModal,
    handleSearch,
    searchQuery,
    filterMinistry,
    filterGender,
    filterAge,
    handleFilterMinistry,
    handleFilterGender,
    handleFilterAge,
    ministries,
    isSaving,
    photoPreview,
    signaturePreview,
    handlePhotoUpload,
    handleSignatureUpload,
  };
}
